/**
 * iter211o — Decline Analytics (lightweight).
 * FOH-facing roll-up over per-item declines captured by the customer
 * portal. Shows: totals, top decline reasons, most-declined items,
 * month-over-month trend. Data lives on
 * ``approval_tokens.response_data.declined_items`` — this page just
 * renders the pre-aggregated payload from
 * ``GET /api/foh/analytics/declines``.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingDown, MessageCircle, PoundSterling, Users, Package } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_COLOURS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308",
  "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6",
];

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n || 0);

const KPI = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-slate-400" />
      <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</span>
    </div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const DeclineAnalyticsPage = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/foh/analytics/declines?days=${days}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load decline analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [days]);

  const totals = data?.totals || {};
  const byReason = data?.by_reason || [];
  const byItem   = data?.by_item   || [];
  const byMonth  = data?.by_month  || [];

  const hasData = totals.declined_items_count > 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="decline-analytics-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate("/app/foh")}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="decline-analytics-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900 leading-tight">Decline Analytics</h1>
            <p className="text-xs text-slate-500">Where estimated value is walking away — and why.</p>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
            data-testid="decline-analytics-window-select"
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
          </select>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {loading && (
          <div className="text-center text-sm text-slate-500 py-16" data-testid="decline-analytics-loading">
            Loading analytics…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm" data-testid="decline-analytics-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <KPI
                testid="kpi-declined-value"
                icon={PoundSterling}
                label="Value walking away"
                value={fmtGBP(totals.declined_total_value)}
                sub={`Across ${totals.declined_items_count || 0} declined items`}
              />
              <KPI
                testid="kpi-declined-customers"
                icon={Users}
                label="Customers with skips"
                value={totals.customers_with_declines || 0}
                sub="Unique portal submissions"
              />
              <KPI
                testid="kpi-declined-items-count"
                icon={Package}
                label="Skipped items"
                value={totals.declined_items_count || 0}
                sub="Individual item declines"
              />
            </div>

            {!hasData && (
              <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="decline-analytics-empty">
                <TrendingDown className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-600 font-medium">No declines in this window.</p>
                <p className="text-xs text-slate-400 mt-1">Widen the date range or check back later.</p>
              </div>
            )}

            {hasData && (
              <>
                {/* Top decline reasons */}
                <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="decline-by-reason-chart">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-4 h-4 text-slate-500" />
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Top decline reasons</h2>
                  </div>
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={byReason} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="label" type="category" width={180} tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v, k) => (k === "value" ? fmtGBP(v) : v)}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Bar dataKey="count" name="Times cited" radius={[0, 4, 4, 0]}>
                          {byReason.map((_, idx) => (
                            <Cell key={idx} fill={REASON_COLOURS[idx % REASON_COLOURS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {byReason.map((r) => (
                      <div key={r.reason_key} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5" data-testid={`decline-reason-row-${r.reason_key}`}>
                        <span className="font-medium text-slate-700 truncate mr-2">{r.label}</span>
                        <span className="text-slate-500 shrink-0">
                          <strong className="text-slate-800">{r.count}</strong> · {fmtGBP(r.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Most-declined items by value */}
                <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="decline-by-item-list">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-slate-500" />
                    <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Most-declined items by value</h2>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {byItem.map((it, idx) => {
                      const max = byItem[0]?.value || 1;
                      const pct = Math.max(6, (it.value / max) * 100);
                      return (
                        <div key={idx} className="py-2" data-testid={`decline-item-row-${idx}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-700 flex-1 pr-2 truncate">{it.label}</span>
                            <span className="text-slate-500 shrink-0">
                              <strong className="text-slate-800">{it.count}×</strong> · {fmtGBP(it.value)}
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-red-400 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Month-over-month */}
                {byMonth.length > 1 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="decline-by-month-chart">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingDown className="w-4 h-4 text-slate-500" />
                      <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Trend</h2>
                    </div>
                    <div style={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer>
                        <LineChart data={byMonth} margin={{ left: 5, right: 20, top: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                          <Tooltip
                            formatter={(v, k) => (k === "value" ? fmtGBP(v) : v)}
                            contentStyle={{ fontSize: 12 }}
                          />
                          <Line yAxisId="left" type="monotone" dataKey="count" name="Skips" stroke="#ef4444" strokeWidth={2} dot />
                          <Line yAxisId="right" type="monotone" dataKey="value" name="Value" stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 4" dot />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeclineAnalyticsPage;
